import React from "react";
import { Link } from "react-router-dom";
import GoogleOAuthButton from "../../components/auth/GoogleOAuthButton";

const inputStyle = {
  width: "100%",
  padding: "12px",
  marginBottom: "12px",
  borderRadius: "8px",
  border: "1px solid #ccc",
  fontSize: "15px",
};

const buttonStyle = {
  width: "100%",
  background: "#1fa4c5",
  border: "none",
  padding: "12px",
  borderRadius: "8px",
  color: "#fff",
  fontSize: "16px",
  fontWeight: "bold",
  cursor: "pointer",
  marginBottom: "14px",
};

const consentSectionStyle = {
  marginTop: "8px",
  marginBottom: "18px",
  padding: "14px 16px",
  borderRadius: "12px",
  border: "1px solid #d8e2ea",
  background: "#f8fbfd",
  textAlign: "left",
};

const consentTitleStyle = {
  margin: "0 0 12px",
  color: "#35566d",
  fontSize: "13px",
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
};

const consentItemStyle = {
  marginBottom: "14px",
};

const consentLabelStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: "10px",
  cursor: "pointer",
  color: "#243746",
  fontSize: "14px",
  lineHeight: 1.5,
};

const consentCheckboxStyle = {
  marginTop: "3px",
  flexShrink: 0,
};

const consentHelperStyle = {
  margin: "8px 0 0 30px",
  color: "#5f7281",
  fontSize: "12px",
  lineHeight: 1.5,
};

const consentLinkStyle = {
  color: "#1f7f99",
  fontWeight: 600,
  textDecoration: "underline",
};

const fieldLabelStyle = {
  display: "block",
  marginBottom: "6px",
  color: "#35566d",
  fontSize: "14px",
  fontWeight: 600,
  textAlign: "left",
};

const fieldErrorStyle = {
  marginTop: "6px",
  color: "#b3261e",
  fontSize: "13px",
  lineHeight: 1.4,
  textAlign: "left",
};

const dropdownListStyle = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  maxHeight: "200px",
  overflowY: "auto",
  background: "#fff",
  border: "1px solid #ccc",
  borderTop: "none",
  borderRadius: "0 0 8px 8px",
  padding: "4px 0",
  zIndex: 1000,
  textAlign: "left",
  boxShadow: "0 6px 14px rgba(0,0,0,0.12)",
};

function renderConsentLabel(consentTextOrLabel, withLegalLinks = false) {
  const label =
    typeof consentTextOrLabel === "string"
      ? consentTextOrLabel
      : consentTextOrLabel?.label || "";

  if (!withLegalLinks) {
    return label;
  }

  const labelParts = consentTextOrLabel?.labelParts;
  if (labelParts) {
    return (
      <>
        {labelParts.beforeTerms}
        <Link to="/legal/terms" style={consentLinkStyle}>
          {labelParts.termsLabel}
        </Link>
        {labelParts.betweenLinks}
        <Link to="/legal/privacy" style={consentLinkStyle}>
          {labelParts.privacyLabel}
        </Link>
        {labelParts.afterPrivacy}
      </>
    );
  }

  return label
    .split(/(Terms and Conditions|Privacy Policy)/)
    .map((part, index) => {
      if (part === "Terms and Conditions") {
        return (
          <Link key={`terms-${index}`} to="/legal/terms" style={consentLinkStyle}>
            {part}
          </Link>
        );
      }

      if (part === "Privacy Policy") {
        return (
          <Link
            key={`privacy-${index}`}
            to="/legal/privacy"
            style={consentLinkStyle}
          >
            {part}
          </Link>
        );
      }

      return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
    });
}

export function RegisterAccountSection({ register }) {
  return (
    <>
      <h2 style={{ marginBottom: "20px", color: "#1fa4c5" }}>
        {register.t("register_account_details_title", {
          defaultValue: "Account details",
        })}
      </h2>

      <p style={{ margin: "0 0 18px", color: "#5f7281", fontSize: "14px" }}>
        {register.t("register_account_details_intro", {
          defaultValue: "Start with the essentials for signing in.",
        })}
      </p>

      <div className="register-language-selector">
        <label
          htmlFor="register-language"
          className="register-language-selector__label"
        >
          {register.t("select_language", { defaultValue: "Select language" })}
        </label>
        <select
          id="register-language"
          value={register.language}
          onChange={(e) => register.handleLanguageChange(e.target.value)}
          className="register-language-selector__select"
        >
          {register.availableLanguages.map((language) => (
            <option key={language.code} value={language.code}>
              {language.label}
            </option>
          ))}
        </select>
      </div>

      <div className="register-oauth-block">
        <GoogleOAuthButton />
        <div className="register-oauth-divider">
          <span>
            {register.t("oauth_or_email", { defaultValue: "or" })}
          </span>
        </div>
      </div>

      <label htmlFor="register-email" style={fieldLabelStyle}>
        {register.t("email")}
      </label>
      <input
        id="register-email"
        name="email"
        autoComplete="email"
        type="email"
        placeholder={`${register.t("email")} *`}
        value={register.email}
        onChange={(e) => register.setEmail(e.target.value)}
        style={inputStyle}
      />

      <label htmlFor="register-username" style={fieldLabelStyle}>
        {register.t("username")}
      </label>
      <input
        id="register-username"
        name="username"
        autoComplete="username"
        type="text"
        placeholder={`${register.t("username")} *`}
        value={register.username}
        onChange={(e) => register.setUsername(e.target.value)}
        style={inputStyle}
      />

      <div style={{ position: "relative" }}>
        <label htmlFor="register-password" style={fieldLabelStyle}>
          {register.t("password")}
        </label>
        <input
          id="register-password"
          name="password"
          autoComplete="new-password"
          type={register.showPassword ? "text" : "password"}
          placeholder={`${register.t("password")} *`}
          value={register.password}
          onChange={(e) => register.setPassword(e.target.value)}
          style={inputStyle}
        />
        <button
          type="button"
          aria-label={register.showPassword ? register.t("hide") : register.t("show")}
          onClick={() => register.setShowPassword((show) => !show)}
          style={{
            position: "absolute",
            right: "14px",
            bottom: "24px",
            cursor: "pointer",
            color: "#1fa4c5",
            fontSize: "13px",
            fontWeight: 600,
            border: 0,
            background: "transparent",
          }}
        >
          {register.showPassword ? register.t("hide") : register.t("show")}
        </button>
      </div>

      <button
        onClick={register.handleStep1}
        style={buttonStyle}
        className="register-continue-btn"
        disabled={register.isStep1Checking}
        aria-busy={register.isStep1Checking}
      >
        <span className="register-submit-button__content">
          {register.isStep1Checking ? (
            <span className="register-submit-spinner" aria-hidden="true" />
          ) : null}
          <span>
            {register.isStep1Checking
              ? register.t("register_step1_checking", {
                  defaultValue: "Checking…",
                })
              : register.t("continue")}
          </span>
        </span>
      </button>

      <p style={{ fontSize: "12px", color: "#777" }}>
        {register.t("password_requirements")}
      </p>

      <p style={{ fontSize: "14px", color: "#555", marginTop: "10px" }}>
        {register.t("have_account")}{" "}
        <Link to="/login" style={{ color: "#1fa4c5", fontWeight: "600" }}>
          {register.t("login_here")}
        </Link>
      </p>
    </>
  );
}

export function RegisterRequiredProfileSection({ register }) {
  const isPersonalSubmit = register.accountType === "personal";
  const isStep2Submitting = isPersonalSubmit && register.isSubmitting;

  return (
    <>
      <h2 style={{ marginBottom: "20px", color: "#1fa4c5" }}>
        {register.t("register_required_profile_title", {
          defaultValue: "Required profile details",
        })}
      </h2>

      <p style={{ margin: "0 0 8px", color: "#5f7281", fontSize: "14px" }}>
        {register.t("register_required_profile_intro", {
          defaultValue: "These details keep your account useful and safe.",
        })}
      </p>
      <p className="register-required-profile-latin-hint">
        {register.t("register_required_profile_latin_hint", {
          defaultValue:
            "For now, please enter country and city using English/Latin letters only.",
        })}
      </p>

      <div style={{ position: "relative", marginBottom: "12px" }}>
        <label htmlFor="register-country" style={fieldLabelStyle}>
          {register.t("country_of_residence", { defaultValue: "Country" })}
        </label>
        <input
          id="register-country"
          type="text"
          placeholder={`${register.t("country_of_residence")} *`}
          value={register.country}
          onChange={(e) => register.handleCountryChange(e.target.value)}
          style={{ ...inputStyle, marginBottom: 0 }}
          autoComplete="off"
        />
        {register.countryValidationError ? (
          <div style={fieldErrorStyle}>{register.countryValidationError}</div>
        ) : null}

        {register.countrySuggestions.length > 0 && (
          <div style={dropdownListStyle}>
            {register.countrySuggestions.map((country) => (
              <div
                key={country}
                onClick={() => register.selectCountrySuggestion(country)}
                style={{
                  padding: "10px 12px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                {country}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ position: "relative", marginBottom: "12px" }}>
        <label htmlFor="register-city" style={fieldLabelStyle}>
          {register.t("city_or_town", { defaultValue: "City" })}
        </label>
        <input
          id="register-city"
          type="text"
          placeholder={`${register.t("city_or_town")} *`}
          value={register.city}
          onChange={(e) => register.handleCityChange(e.target.value)}
          style={{ ...inputStyle, marginBottom: 0 }}
          autoComplete="off"
        />
        {register.cityValidationError ? (
          <div style={fieldErrorStyle}>{register.cityValidationError}</div>
        ) : null}

        {register.citySuggestions.length > 0 && (
          <div style={dropdownListStyle}>
            {register.citySuggestions.map((cityOption) => (
              <div
                key={
                  typeof cityOption === "string"
                    ? cityOption
                    : `${cityOption.name || "city"}-${cityOption.country || ""}-${cityOption.lat ?? ""}-${cityOption.lng ?? ""}`
                }
                onClick={() => register.selectCitySuggestion(cityOption)}
                style={{
                  padding: "10px 12px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                {register.getCitySuggestionLabel(cityOption)}
              </div>
            ))}
          </div>
        )}
      </div>

      <label htmlFor="register-account-type" style={fieldLabelStyle}>
        {register.t("account_type", { defaultValue: "Account type" })}
      </label>
      <select
        id="register-account-type"
        value={register.accountType}
        onChange={(e) => register.onChangeAccountType(e.target.value)}
        style={inputStyle}
      >
        <option value="">{register.t("select_account_type")} *</option>
        <option value="personal">{register.t("personal")}</option>
        <option value="business">{register.t("business")}</option>
      </select>

      <div style={consentSectionStyle}>
        <div style={consentTitleStyle}>
          {register.t("register_required_consent", {
            defaultValue: "Required consent",
          })}
        </div>

        <div style={consentItemStyle}>
          <div style={consentLabelStyle}>
            <input
              type="checkbox"
              checked={register.acceptTermsPrivacy}
              onChange={(e) => register.setAcceptTermsPrivacy(e.target.checked)}
              style={consentCheckboxStyle}
            />
            <span>
              {renderConsentLabel(
                register.consentTexts.acceptTermsPrivacy,
                true
              )}
            </span>
          </div>
          <p style={consentHelperStyle}>
            {register.consentTexts.acceptTermsPrivacy.helper}
          </p>
        </div>

        <div style={{ ...consentItemStyle, marginBottom: 0 }}>
          <label style={consentLabelStyle}>
            <input
              type="checkbox"
              checked={register.acceptProfileVisibility}
              onChange={(e) =>
                register.setAcceptProfileVisibility(e.target.checked)
              }
              style={consentCheckboxStyle}
            />
            <span>{register.consentTexts.acceptProfileVisibility.label}</span>
          </label>
          <p style={consentHelperStyle}>
            {register.consentTexts.acceptProfileVisibility.helper}
          </p>
        </div>
      </div>

      <button
        onClick={register.handleStep2}
        style={buttonStyle}
        disabled={isStep2Submitting}
        aria-busy={isStep2Submitting}
      >
        <span className="register-submit-button__content">
          {isStep2Submitting ? (
            <span className="register-submit-spinner" aria-hidden="true" />
          ) : null}
          <span>
            {isStep2Submitting
              ? register.t("register_submit_loading", {
                  defaultValue: "Submitting...",
                })
              : isPersonalSubmit
              ? register.t("complete_registration", {
                  defaultValue: "Complete registration",
                })
              : register.t("continue")}
          </span>
        </span>
      </button>

      <button
        onClick={() => register.setStep(1)}
        style={{ ...buttonStyle, background: "#777" }}
        disabled={isStep2Submitting}
      >
        {register.t("back")}
      </button>
    </>
  );
}
